import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from './Modal';
import { ShortcutLinks } from './ShortcutLinks';
import { UploadZone } from './UploadZone';
import { EmailMode } from './EmailMode';
import { FetchHub } from './FetchHub';
import { BrokerTile } from './BrokerTile';
import { ErrorView } from './ErrorView';
import { SuccessSummary } from './SuccessSummary';
import { HomeLayout } from './HomeLayouts';
import {
  Button,
  OptionCard,
  ScreenHeader,
} from './ui';
import {
  IconShield,
  IconBolt,
  IconUpload,
  IconMail,
  IconInboxScan,
  IconForwardEmail,
  IconGeneratorMailback,
} from './icons';
import {
  createApiClient,
  parseStatement,
  parseStatementFromUrl,
  generateCAS,
  cdslFetchRequestOtp,
  cdslFetchVerifyOtp,
  GENERATOR_DEFAULT_PASSWORD,
} from '../utils/api';
import { classifyError, type ClassifiedError } from '../utils/errors';
import type {
  PortfolioConnectConfig,
  ParsedData,
  PortfolioConnectEvent,
  PortfolioConnectError,
  BrokerInfo,
  InboxFile,
  InboundEmailFile,
  PortfolioType,
} from '../types';

const DEFAULT_LOGO_URL =
  'https://casparser.in/images/logos/casparser-icon.png';

const CAS_PORTALS: Record<PortfolioType, { name: string; url: string }> = {
  CAMS_KFINTECH: {
    // The portal happens to be hosted by KFintech, but for end users we
    // surface this as "the mutual fund CAS portal".
    name: 'the mutual fund CAS portal',
    url: 'https://mfs.kfintech.com/investor/General/ConsolidatedAccountStatement',
  },
  CDSL: { name: 'CDSL', url: 'https://www.cdslindia.com/cas/logincas.aspx' },
  NSDL: { name: 'NSDL', url: 'https://eservices.nsdl.com/SecureWeb/HomeLogin.jsp' },
};

// Default broker list with recognizable logos. The smallcase CDN hosts
// SVGs for every major Indian broker — much better visual recognition
// than initial-letter avatars, which matters for conversion. The same
// CDN base is used by the v1.x SDK; URLs are verified to return 200.
// Embedders can override the entire list (and supply their own logos)
// via `config.brokers`. Missing/broken logo URLs fall back to the
// deterministic color-initial avatar rendered by `BrokerTile`.
const BROKER_LOGO_BASE =
  'https://assets.smallcase.com/smallcase/assets/brokerLogo/small';
const brokerLogo = (slug: string) => `${BROKER_LOGO_BASE}/${slug}.svg`;

const DEFAULT_BROKERS: BrokerInfo[] = (
  [
    { name: 'Zerodha', depository: 'CDSL' as const, logo: brokerLogo('kite') },
    { name: 'Groww', depository: 'CDSL' as const, logo: brokerLogo('groww') },
    { name: 'Angel One', depository: 'CDSL' as const, logo: brokerLogo('angelbroking') },
    { name: 'Upstox', depository: 'CDSL' as const, logo: brokerLogo('upstox') },
    { name: '5paisa', depository: 'CDSL' as const, logo: brokerLogo('fivepaisa') },
    { name: 'Dhan', depository: 'CDSL' as const, logo: brokerLogo('dhan') },
    { name: 'Alice Blue', depository: 'CDSL' as const, logo: brokerLogo('aliceblue') },
    { name: 'Fisdom', depository: 'CDSL' as const, logo: brokerLogo('fisdom') },
    { name: 'FundzBazar', depository: 'CDSL' as const, logo: brokerLogo('fundzbazar') },
    { name: 'HDFC SKY', depository: 'CDSL' as const, logo: brokerLogo('hdfcsky') },
    { name: 'ICICI Direct', depository: 'NSDL' as const, logo: brokerLogo('icici') },
    { name: 'HDFC Securities', depository: 'NSDL' as const, logo: brokerLogo('hdfc') },
    { name: 'Kotak Securities', depository: 'NSDL' as const, logo: brokerLogo('kotak') },
    { name: 'SBI Securities', depository: 'NSDL' as const, logo: brokerLogo('sbi') },
    { name: 'Motilal Oswal', depository: 'NSDL' as const, logo: brokerLogo('motilal') },
    { name: 'IIFL Capital', depository: 'NSDL' as const, logo: brokerLogo('iifl') },
    // Nuvama is the rebrand of Edelweiss Wealth — the CDN still serves it
    // under the old slug.
    { name: 'Nuvama Wealth', depository: 'NSDL' as const, logo: brokerLogo('edelweiss') },
    { name: 'Trustline', depository: 'NSDL' as const, logo: brokerLogo('trustline') },
    { name: 'AxisDirect', depository: 'NSDL' as const, logo: brokerLogo('axis') },
  ]
).sort((a, b) => a.name.localeCompare(b.name));

interface PortfolioConnectWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: string;
  apiBaseUrl?: string;
  config?: PortfolioConnectConfig;
  onSubmit?: (
    input: import('../types').SubmitInput,
    password: string,
    onProgress?: (percent: number) => void,
  ) => Promise<any>;
  onSuccess: (data: ParsedData, metadata: any) => void;
  onError?: (error: PortfolioConnectError) => void;
  onEvent?: (event: PortfolioConnectEvent, metadata: any) => void;
}

type WidgetMode =
  | 'HOME'
  | 'FILE_UPLOAD'
  | 'EMAIL'
  | 'FETCH_HUB'
  | 'BROKER_PICKER'
  // Two-section method picker ("I have the file" / "Get it for me").
  // Skipped when only one method is viable.
  | 'DEMAT_METHOD'
  | 'MF_METHOD'
  | 'SUCCESS';

export const PortfolioConnectWidget: React.FC<PortfolioConnectWidgetProps> = ({
  isOpen,
  onClose,
  apiKey,
  apiBaseUrl,
  config = {},
  onSubmit,
  onSuccess,
  onError,
  onEvent,
}) => {
  const [mode, setMode] = useState<WidgetMode>('HOME');
  const [selectedType, setSelectedType] = useState<PortfolioType>('CAMS_KFINTECH');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatorLoading, setGeneratorLoading] = useState(false);
  const [classifiedError, setClassifiedError] = useState<ClassifiedError | null>(
    null,
  );
  const [errorSourceMode, setErrorSourceMode] = useState<WidgetMode>('FILE_UPLOAD');
  const [selectedBroker, setSelectedBroker] = useState<string | null>(null);
  // Did the user reach FILE_UPLOAD through the broker picker? Drives the
  // Back button behaviour so they return to the picker (preserving their
  // selection context) instead of jumping all the way back to HOME.
  const [cameViaBrokerPicker, setCameViaBrokerPicker] = useState(false);
  const [emailDefaultTab, setEmailDefaultTab] = useState<'CONNECT' | 'FORWARD' | undefined>(undefined);
  const [fetchDefaultTab, setFetchDefaultTab] = useState<'CDSL' | 'KFINTECH' | undefined>(undefined);
  const [prefillPassword, setPrefillPassword] = useState<string>('');
  // File dropped/picked on the home screen (unified layout) — forwarded to
  // UploadZone as its initial file so the user lands on the password step
  // without re-picking.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [successPayload, setSuccessPayload] = useState<{
    data: ParsedData;
    metadata: any;
  } | null>(null);

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Cancelled on unmount so a mid-delay close doesn't fire handleClose on
  // a dead component (would emit WIDGET_CLOSED twice).
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set true once a parser reports real progress; the simulated interval
  // then no-ops so it doesn't jitter backwards over the real value.
  const realProgressRef = useRef(false);
  // Back-navigation stack. `switchMode` pushes the prior mode when
  // entering EMAIL/FETCH_HUB/FILE_UPLOAD from a non-HOME mode; `handleBack`
  // pops to restore broker/upload context instead of always going HOME.
  const navStackRef = useRef<WidgetMode[]>([]);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const apiClient = createApiClient(apiKey, apiBaseUrl);

  const emitEvent = (event: PortfolioConnectEvent, metadata: any = {}) => {
    onEventRef.current?.(event, { timestamp: Date.now(), ...metadata });
  };

  const allowedTypes = config.allowedTypes || ['CAMS_KFINTECH', 'CDSL', 'NSDL'];
  const enableGenerator = !!config.enableGenerator;
  const enableCdslFetch = !!config.enableCdslFetch;
  const enableInbox = !!config.enableInbox;
  const enableInboundEmail = !!config.enableInboundEmail;
  const showShortcuts = config.showShortcuts !== false;
  const showPortalLinks = config.showPortalLinks !== false;
  // Defaults to true. See PortfolioConnectConfig.showBrokerPicker JSDoc.
  const showBrokerPicker = config.showBrokerPicker !== false;
  // Defaults to 'close' — host page owns the post-import experience.
  // Pass 'summary' to render <SuccessSummary> inside the widget instead.
  const successBehavior = config.successBehavior ?? 'close';

  const emailAvailable = enableInbox || enableInboundEmail;
  const fetchAvailable =
    (enableCdslFetch && allowedTypes.includes('CDSL')) ||
    (enableGenerator && allowedTypes.includes('CAMS_KFINTECH'));

  useEffect(() => {
    if (!isOpen) return;
    // Reset state machine for a fresh session.
    const first = allowedTypes[0] || 'CAMS_KFINTECH';
    setSelectedType(first);
    setMode('HOME');
    setIsProcessing(false);
    setProgress(0);
    setClassifiedError(null);
    setSuccessPayload(null);
    setSelectedBroker(null);
    setCameViaBrokerPicker(false);
    setPendingFile(null);
    setPrefillPassword('');
    setEmailDefaultTab(undefined);
    setFetchDefaultTab(undefined);
    navStackRef.current = [];
    realProgressRef.current = false;
    emitEvent('WIDGET_OPENED');

    // Surface common config mistakes at mount in dev builds instead of
    // letting the user hit the runtime guard deep in the OAuth flow.
    if (
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV !== 'production' &&
      enableInbox &&
      !config.inbox?.redirectUri
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Portfolio Connect] enableInbox is on but config.inbox.redirectUri is missing. ' +
          'Inbox import will fail at the connect step. See ' +
          'https://casparser.in/docs#gmail-oauth-setup',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    emitEvent('WIDGET_CLOSED');
    onClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const switchMode = (m: WidgetMode) => {
    // Record forward transitions into EMAIL / FETCH_HUB / FILE_UPLOAD
    // when they originate from a non-HOME mode. Lets `handleBack`
    // restore the prior screen (e.g. demat method picker, upload
    // screen) instead of always defaulting to HOME and losing the
    // broker/upload context the user just established.
    const TRACKED: WidgetMode[] = ['EMAIL', 'FETCH_HUB', 'FILE_UPLOAD'];
    if (TRACKED.includes(m) && mode !== 'HOME' && mode !== m) {
      navStackRef.current.push(mode);
    } else if (m === 'HOME') {
      // Returning to HOME unwinds the entire stack — the user is
      // restarting their journey from the top. Also drop any seeded
      // generator state so a previous mailback's password (or a file
      // dropped on the unified-home zone earlier) doesn't pre-fill an
      // unrelated subsequent upload session.
      navStackRef.current = [];
      setPrefillPassword('');
      setPendingFile(null);
      setCameViaBrokerPicker(false);
      setSelectedBroker(null);
    }
    // Clear default-tab hints when leaving their owning hub so they
    // don't linger and trigger a spurious mid-flow close-confirm later.
    if (mode === 'FETCH_HUB' && m !== 'FETCH_HUB') {
      setFetchDefaultTab(undefined);
    }
    if (mode === 'EMAIL' && m !== 'EMAIL') {
      setEmailDefaultTab(undefined);
    }
    emitEvent('MODE_SWITCHED', { from: mode, to: m });
    setMode(m);
  };

  // ---------- Parse handlers ----------

  const runParse = async (
    parser: () => Promise<any>,
    eventMeta: Record<string, any>,
    source: WidgetMode,
  ) => {
    setIsProcessing(true);
    setProgress(10);
    realProgressRef.current = false;
    // Simulated progress so the user sees movement during the parse.
    // The moment a parser reports real progress via its onProgress
    // callback, `realProgressRef.current` flips true and this interval
    // becomes a no-op — prevents the simulated value from jittering
    // backwards over the real one.
    progressIntervalRef.current = setInterval(() => {
      if (realProgressRef.current) return;
      setProgress((p) => {
        const next = Math.min(p + Math.random() * 10, 90);
        emitEvent('UPLOAD_PROGRESS', { percent: Math.round(next) });
        return next;
      });
    }, 500);
    try {
      const start = Date.now();
      const response = await parser();
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      // Defensive: a parser that fires `onProgress` after the await
      // resolves (none today, but the contract permits it) shouldn't
      // be able to tick the bar backwards from 100%.
      setProgress((p) => Math.max(p, 100));
      // Real /v4/smart/parse response shape:
      //   { meta: { cas_type, generated_at, statement_period }, investor: {...},
      //     mutual_funds: [...], demat_accounts: [...], insurance: {...},
      //     nps: [...], summary: { total_value, accounts: {...} } }
      // Older callers may have wrapped data under `data` — both are
      // probed for safety.
      const detectedCasType =
        response?.meta?.cas_type ||
        response?.data?.meta?.cas_type ||
        response?.cas_type || // legacy / synthesised responses
        null;
      const metadata = {
        ...eventMeta,
        parser_type: detectedCasType || eventMeta.parser_type || 'AUTO',
        cas_type: detectedCasType || undefined,
        parse_duration_ms: Date.now() - start,
      };
      emitEvent('PARSE_SUCCESS', metadata);
      handleParseSuccess(response, metadata);
    } catch (err) {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setIsProcessing(false);
      setProgress(0);
      const classified = classifyError(err, 'PARSE_ERROR');
      setErrorSourceMode(source);
      setClassifiedError(classified);
      const casError: PortfolioConnectError = {
        code: classified.code,
        message: classified.message,
        title: classified.title,
        remediation: classified.remediation,
        retryable: classified.retryable,
        details: classified.raw,
      };
      emitEvent('PARSE_ERROR', { error: casError });
      onError?.(casError);
    }
  };

  const handleParseSuccess = (response: ParsedData, metadata: any) => {
    // Fire the developer callback immediately in BOTH success modes so
    // host-page code can start rendering its post-import UI without
    // waiting on the widget's teardown animation. The 400ms delay below
    // only governs the visual close — the data is already in the host's
    // hands by then.
    onSuccess(response, metadata);

    if (successBehavior === 'close') {
      // Brief 400ms delay so the user sees the 100% progress flash
      // before the modal disappears. Stash the timer in a ref so the
      // unmount cleanup can cancel it — without this, closing the
      // modal during the delay (e.g. via the X button) calls
      // handleClose on a dead component and emits WIDGET_CLOSED twice.
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = setTimeout(() => {
        closeTimeoutRef.current = null;
        handleClose();
      }, 400);
      return;
    }
    setSuccessPayload({ data: response, metadata });
    setMode('SUCCESS');
  };

  // Flips the realProgress flag the first time a parser reports actual
  // progress, so the simulated-progress interval bows out and stops
  // racing the real value.
  const reportProgress = (p: number) => {
    realProgressRef.current = true;
    setProgress(p);
  };

  const handleFileProcess = async (file: File, password: string) => {
    emitEvent('UPLOAD_STARTED', { filename: file.name, size: file.size });
    await runParse(
      () =>
        onSubmit
          ? onSubmit(
              { kind: 'file', file, filename: file.name, source: 'UPLOAD' },
              password,
              reportProgress,
            )
          : parseStatement(apiClient, file, password, reportProgress),
      { filename: file.name, source: 'UPLOAD' },
      'FILE_UPLOAD',
    );
  };

  const handleInboxFileSelect = async (file: InboxFile, password: string) => {
    emitEvent('PARSE_STARTED', { filename: file.filename, source: 'INBOX' });
    await runParse(
      () =>
        onSubmit
          ? onSubmit(
              {
                kind: 'url',
                pdfUrl: file.url,
                filename: file.filename,
                source: 'INBOX',
                metadata: {
                  cas_type: file.cas_type,
                  message_id: file.message_id,
                },
              },
              password,
              reportProgress,
            )
          : parseStatementFromUrl(apiClient, file.url, password, reportProgress),
      {
        filename: file.filename,
        source: 'INBOX',
        cas_type: file.cas_type,
        message_id: file.message_id,
        parser_type: file.cas_type.toUpperCase(),
      },
      'EMAIL',
    );
  };

  const handleInboundEmailFileSelect = async (
    file: InboundEmailFile,
    password: string,
  ) => {
    emitEvent('PARSE_STARTED', {
      filename: file.filename,
      source: 'INBOUND_EMAIL',
    });
    await runParse(
      () =>
        onSubmit
          ? onSubmit(
              {
                kind: 'url',
                pdfUrl: file.url,
                filename: file.filename,
                source: 'INBOUND_EMAIL',
                metadata: {
                  cas_type: file.cas_type,
                  message_id: file.message_id,
                  received_at: file.received_at,
                },
              },
              password,
              reportProgress,
            )
          : parseStatementFromUrl(apiClient, file.url, password, reportProgress),
      {
        filename: file.filename,
        source: 'INBOUND_EMAIL',
        cas_type: file.cas_type,
        message_id: file.message_id,
        received_at: file.received_at,
        parser_type: file.cas_type.toUpperCase(),
      },
      'EMAIL',
    );
  };

  // ---------- Fetch flows ----------

  const handleGeneratorSubmit = async (pan: string, email: string) => {
    setGeneratorLoading(true);
    emitEvent('GENERATOR_STARTED', { pan: pan.slice(0, 4) + '***' });
    try {
      await generateCAS(apiClient, email, pan, config.generator);
      emitEvent('GENERATOR_SUCCESS');
      setGeneratorLoading(false);
      // Pre-fill the upload screen's password field with the *exact*
      // password we asked the registrar to encrypt the CAS with —
      // either the embedder's override or the SDK default. Previously
      // we fell back to '' here, which left the field empty even though
      // the CAS the user is about to receive is encrypted with the
      // SDK's default password (mismatch the user couldn't recover from
      // unless they happened to know the magic string).
      const effectivePassword =
        config.generator?.password || GENERATOR_DEFAULT_PASSWORD;
      setPrefillPassword(effectivePassword);
      setSelectedType('CAMS_KFINTECH');
      // FetchMode now owns the post-success confirmation screen; we hand
      // off to FILE_UPLOAD only when the user taps "OK, take me to upload".
    } catch (err) {
      setGeneratorLoading(false);
      const classified = classifyError(err, 'GENERATOR_ERROR');
      emitEvent('GENERATOR_ERROR', {
        error: { code: classified.code, message: classified.message },
      });
      throw new Error(classified.message);
    }
  };

  const handleGeneratorContinueToUpload = () => {
    setFetchDefaultTab(undefined);
    switchMode('FILE_UPLOAD');
  };

  // Cross-flow: user requested a mailback, and inbound email is enabled —
  // jump them to the inbound-email screen so the forwarding address is
  // ready when the CAS arrives. We emit a dedicated event so embedders
  // can measure how often this nudge converts.
  const handleGeneratorJumpToInbound = (mailbackEmail: string) => {
    emitEvent('GENERATOR_TO_INBOUND_HANDOFF', { destination_email: mailbackEmail });
    setFetchDefaultTab(undefined);
    setEmailDefaultTab('FORWARD');
    switchMode('EMAIL');
  };

  const handleCdslRequestOtp = async (
    pan: string,
    boId: string,
    dob: string,
  ): Promise<string> => {
    emitEvent('CDSL_FETCH_STARTED', { pan: pan.slice(0, 4) + '***' });
    try {
      const res = await cdslFetchRequestOtp(apiClient, pan, boId, dob);
      emitEvent('CDSL_OTP_SENT', { session_id: res.session_id });
      return res.session_id;
    } catch (err) {
      const classified = classifyError(err, 'CDSL_FETCH_ERROR');
      emitEvent('CDSL_FETCH_ERROR', {
        error: { code: classified.code, message: classified.message },
      });
      throw new Error(classified.message);
    }
  };

  const handleCdslVerifyOtp = async (
    sessionId: string,
    otp: string,
    pan: string,
  ): Promise<void> => {
    try {
      const res = await cdslFetchVerifyOtp(apiClient, sessionId, otp);
      emitEvent('CDSL_OTP_VERIFIED');
      emitEvent('CDSL_FETCH_SUCCESS', {
        files: res.files,
        count: res.files.length,
      });
      if (!res.files?.length) {
        throw new Error(
          'CDSL didn\'t return any statements for this account. Double-check your details and try again.',
        );
      }
      const target = res.files[0];
      emitEvent('PARSE_STARTED', {
        filename: target.filename,
        source: 'CDSL_FETCH',
      });
      // CDSL CAS PDFs are encrypted with the investor's PAN — the SDK
      // passes it as the password for both the parse path and the
      // collect-only `onSubmit` path so embedders can decrypt them
      // server-side too.
      await runParse(
        () =>
          onSubmit
            ? onSubmit(
                {
                  kind: 'url',
                  pdfUrl: target.url,
                  filename: target.filename,
                  source: 'CDSL_FETCH',
                  metadata: {
                    all_files: res.files,
                  },
                },
                pan,
                reportProgress,
              )
            : parseStatementFromUrl(apiClient, target.url, pan, reportProgress),
        {
          filename: target.filename,
          source: 'CDSL_FETCH',
          all_files: res.files,
          parser_type: 'CDSL',
        },
        'FETCH_HUB',
      );
    } catch (err) {
      const classified = classifyError(err, 'CDSL_FETCH_ERROR');
      emitEvent('CDSL_FETCH_ERROR', {
        error: { code: classified.code, message: classified.message },
      });
      throw new Error(classified.message);
    }
  };

  // ---------- Navigation ----------

  const handleRetry = () => {
    setClassifiedError(null);
    setMode(errorSourceMode);
  };

  // True when the demat method picker has more than one viable option
  // (CDSL OTP, Gmail, inbound, or just upload). Drives both the
  // post-broker routing and the FILE_UPLOAD back-nav target.
  const dematMethodPickerWorthShowing = (depository: 'CDSL' | 'NSDL') => {
    const cdslOtp =
      depository === 'CDSL' && enableCdslFetch && allowedTypes.includes('CDSL');
    return cdslOtp || enableInbox || enableInboundEmail;
  };

  const routeAfterBrokerSelected = (depository: 'CDSL' | 'NSDL') => {
    if (dematMethodPickerWorthShowing(depository)) {
      switchMode('DEMAT_METHOD');
    } else {
      switchMode('FILE_UPLOAD');
    }
  };

  const handleBack = () => {
    // From DEMAT_METHOD: return to the broker picker (preserve selection).
    if (mode === 'DEMAT_METHOD') {
      setMode('BROKER_PICKER');
      emitEvent('MODE_SWITCHED', { from: mode, to: 'BROKER_PICKER' });
      return;
    }
    // From FILE_UPLOAD: if the user came in via the broker picker, return
    // either to the method picker (when it was shown) or straight to the
    // broker picker. This takes precedence over the nav stack because
    // the broker context is more specific than any prior tracked mode.
    if (mode === 'FILE_UPLOAD' && cameViaBrokerPicker) {
      setCameViaBrokerPicker(false);
      const dest =
        selectedType && (selectedType === 'CDSL' || selectedType === 'NSDL') &&
        dematMethodPickerWorthShowing(selectedType)
          ? 'DEMAT_METHOD'
          : 'BROKER_PICKER';
      // `switchMode('FILE_UPLOAD')` pushed the prior mode onto the
      // nav-stack on the way in (e.g. DEMAT_METHOD when the user
      // chose "Upload statement"). Since we're explicitly routing
      // back via the cameViaBrokerPicker branch (not via stack pop),
      // drop that entry if it matches the destination — otherwise
      // it would linger and the next back press would target a
      // phantom DEMAT_METHOD.
      const top = navStackRef.current[navStackRef.current.length - 1];
      if (top === dest || top === 'BROKER_PICKER' || top === 'DEMAT_METHOD') {
        navStackRef.current.pop();
      }
      setMode(dest);
      emitEvent('MODE_SWITCHED', { from: mode, to: dest });
      return;
    }
    // Pop the nav stack — restores EMAIL / FETCH_HUB / FILE_UPLOAD's
    // original predecessor (e.g. user entered EMAIL from the
    // upload-screen "forward by email" alternative → back goes to
    // FILE_UPLOAD, not HOME).
    const previous = navStackRef.current.pop();
    if (previous) {
      setMode(previous);
      emitEvent('MODE_SWITCHED', { from: mode, to: previous });
      return;
    }
    setMode('HOME');
    emitEvent('MODE_SWITCHED', { from: mode, to: 'HOME' });
  };

  // ---------- Render ----------

  const isSandbox = apiKey === 'sandbox-with-json-responses';
  const isSubScreen = mode !== 'HOME' && mode !== 'SUCCESS';

  const renderContent = () => {
    if (classifiedError) {
      return (
        <ErrorView
          error={classifiedError}
          onRetry={classifiedError.retryable ? handleRetry : undefined}
          onClose={handleClose}
        />
      );
    }

    if (mode === 'SUCCESS' && successPayload) {
      return (
        <SuccessSummary
          data={successPayload.data}
          autoCloseMs={config.successAutoCloseMs}
          cta={config.successCta}
          onClose={handleClose}
        />
      );
    }

    if (mode === 'FILE_UPLOAD') {
      // Show "or fetch instantly via OTP" as an inline alternative when
      // the user came in via the demat path with a CDSL broker. This is
      // stronger than the generic portal link below — it's a one-tap
      // jump into the OTP fetch flow with broker context preserved.
      const showCdslFetchAlternative =
        !isProcessing &&
        cameViaBrokerPicker &&
        selectedType === 'CDSL' &&
        enableCdslFetch &&
        allowedTypes.includes('CDSL');

      // Show "or forward by email" as a universal alternative whenever
      // inbound email is enabled. The upload screen is reached from
      // many entry points (actions layout, unified drop, demat broker
      // path) and forwarding works for all of them — if the user has
      // the CAS in an inbox somewhere, they can forward it instead of
      // hunting for the file on disk.
      const showInboundAlternative = !isProcessing && enableInboundEmail;

      return (
        <>
          <UploadZone
            onFileSelect={handleFileProcess}
            isProcessing={isProcessing}
            progress={progress}
            initialPassword={prefillPassword}
            initialFile={pendingFile}
            onFileChange={setPendingFile}
            onPasswordChange={setPrefillPassword}
            onEvent={(event, metadata) =>
              emitEvent(event as PortfolioConnectEvent, metadata)
            }
          />
          {showCdslFetchAlternative && (
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                backgroundColor: 'var(--pc-color-primary-soft)',
                border: '1px solid var(--pc-color-primary-soft-border)',
                borderRadius: 'var(--pc-radius-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  color: 'var(--pc-color-primary)',
                }}
              >
                <IconBolt size={18} />
              </span>
              <div style={{ flex: 1, fontSize: 12, lineHeight: 1.45 }}>
                <strong style={{ color: 'var(--pc-color-text)' }}>
                  Don't have the file?
                </strong>{' '}
                <span style={{ color: 'var(--pc-color-text-muted)' }}>
                  Fetch via CDSL OTP — full demat (CDSL + NSDL) in seconds.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFetchDefaultTab('CDSL');
                  switchMode('FETCH_HUB');
                }}
                style={{
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--pc-color-primary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: 4,
                }}
              >
                Fetch →
              </button>
            </div>
          )}
          {showInboundAlternative && (
            // Mirrors the CDSL fetch card visually but routes to the
            // inbound-email tab. Useful when the user has the CAS in
            // their inbox but not on disk — forwarding the email is
            // faster than downloading and re-uploading.
            <div
              style={{
                marginTop: 10,
                padding: '12px 14px',
                backgroundColor: 'var(--pc-color-surface-subtle)',
                border: '1px solid var(--pc-color-border)',
                borderRadius: 'var(--pc-radius-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  color: 'var(--pc-color-text-muted)',
                }}
              >
                <IconMail size={18} />
              </span>
              <div style={{ flex: 1, fontSize: 12, lineHeight: 1.45 }}>
                <strong style={{ color: 'var(--pc-color-text)' }}>
                  Got it in your email?
                </strong>{' '}
                <span style={{ color: 'var(--pc-color-text-muted)' }}>
                  Forward the email instead — we'll grab the attachment.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  emitEvent('UPLOAD_TO_INBOUND_HANDOFF');
                  setEmailDefaultTab('FORWARD');
                  switchMode('EMAIL');
                }}
                style={{
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--pc-color-primary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: 4,
                }}
              >
                Forward →
              </button>
            </div>
          )}
          {!isProcessing && showShortcuts && (
            <div style={{ marginTop: 16 }}>
              <ShortcutLinks
                selectedType={selectedType}
                onSearchClick={(provider) =>
                  emitEvent('SEARCH_CLICKED', { provider })
                }
              />
            </div>
          )}
          {!isProcessing && showPortalLinks && (
            <div style={{ marginTop: 14, textAlign: 'center' }}>
              <a
                href={CAS_PORTALS[selectedType].url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  emitEvent('PORTAL_CLICKED', { portal: selectedType })
                }
                style={{
                  fontSize: 13,
                  color: 'var(--pc-color-text-muted)',
                  textDecoration: 'none',
                }}
              >
                Don't have your statement?{' '}
                <span style={{ color: 'var(--pc-color-primary)' }}>
                  Download from {CAS_PORTALS[selectedType].name}
                </span>
              </a>
            </div>
          )}
        </>
      );
    }

    if (mode === 'EMAIL' && emailAvailable) {
      return (
        <EmailMode
          apiKey={apiKey}
          apiBaseUrl={apiBaseUrl}
          apiClient={apiClient}
          inboxConfig={config.inbox}
          inboundEmailConfig={config.inboundEmail}
          prefillPan={config.prefill?.pan}
          // Pass the stashed generator password through. When the user
          // arrived here via the generator → inbound handoff, this is
          // the exact password the registrar encrypted the CAS with.
          // For other entry points it's empty and InboundEmailMode
          // falls back to PAN.
          prefillPassword={prefillPassword || undefined}
          enableInbox={enableInbox}
          enableInboundEmail={enableInboundEmail}
          enableGenerator={
            enableGenerator && allowedTypes.includes('CAMS_KFINTECH')
          }
          defaultTab={emailDefaultTab}
          onInboxFileSelect={handleInboxFileSelect}
          onInboundFileSelect={handleInboundEmailFileSelect}
          onJumpToRequestCas={() => {
            // Cross-flow: empty Gmail inbox → request a fresh CAS instead.
            emitEvent('INBOX_TO_GENERATOR_HANDOFF');
            setEmailDefaultTab(undefined);
            setFetchDefaultTab('KFINTECH');
            switchMode('FETCH_HUB');
          }}
          onJumpToUpload={() => {
            // Cross-flow: inbound-email screen → "I already have the file".
            emitEvent('INBOUND_TO_UPLOAD_HANDOFF');
            setEmailDefaultTab(undefined);
            switchMode('FILE_UPLOAD');
          }}
          onBack={() => {
            setEmailDefaultTab(undefined);
            switchMode('HOME');
          }}
          onEvent={(event, metadata) =>
            emitEvent(event as PortfolioConnectEvent, metadata)
          }
        />
      );
    }

    if (mode === 'FETCH_HUB' && fetchAvailable) {
      return (
        <FetchHub
          enableCdslFetch={enableCdslFetch && allowedTypes.includes('CDSL')}
          enableGenerator={
            enableGenerator && allowedTypes.includes('CAMS_KFINTECH')
          }
          defaultTab={fetchDefaultTab}
          prefill={config.prefill}
          onCdslRequestOtp={handleCdslRequestOtp}
          onCdslVerifyOtp={handleCdslVerifyOtp}
          onGeneratorSubmit={handleGeneratorSubmit}
          onGeneratorContinueToUpload={handleGeneratorContinueToUpload}
          onGeneratorJumpToInbound={
            enableInboundEmail ? handleGeneratorJumpToInbound : undefined
          }
          onEvent={(event, metadata) =>
            emitEvent(event as PortfolioConnectEvent, metadata)
          }
          onBack={() => {
            setFetchDefaultTab(undefined);
            switchMode('HOME');
          }}
          generatorLoading={generatorLoading}
          // Show the password the registrar will encrypt the mailed CAS
          // with so users can open the PDF directly from their email
          // client. `prefillPassword` is set on generator success to the
          // exact password used for the request.
          generatorCasPassword={prefillPassword || undefined}
        />
      );
    }

    if (mode === 'BROKER_PICKER') {
      return renderBrokerPicker();
    }

    if (mode === 'DEMAT_METHOD') {
      return renderDematMethod();
    }

    if (mode === 'MF_METHOD') {
      return renderMfMethod();
    }

    // HOME
    return renderHome();
  };

  const renderHome = () => {
    const logoUrl = config.logoUrl || DEFAULT_LOGO_URL;
    const title = config.title || 'Import your investments';
    const subtitle =
      config.subtitle || 'Mutual Funds, Stocks, Bonds — all in one place';
    const homeLayout = config.homeLayout || 'actions';
    const allowMutualFunds = allowedTypes.includes('CAMS_KFINTECH');
    const allowDemat =
      allowedTypes.includes('CDSL') || allowedTypes.includes('NSDL');

    return (
      <HomeLayout
        variant={homeLayout}
        logoUrl={logoUrl}
        title={title}
        subtitle={subtitle}
        emailAvailable={emailAvailable}
        fetchAvailable={fetchAvailable}
        enableInbox={enableInbox}
        enableInboundEmail={enableInboundEmail}
        enableCdslFetch={enableCdslFetch}
        enableGenerator={enableGenerator}
        allowMutualFunds={allowMutualFunds}
        allowDemat={allowDemat}
        onPickFile={() => {
          // Asset-agnostic entry point — the user might have an MF CAS or
          // a demat statement, so we drop them straight into the upload
          // zone. Broker picker is reserved for explicit demat paths
          // (asset-type layout's `onPickDemat`) where the asset class is
          // already known.
          switchMode('FILE_UPLOAD');
        }}
        onPickEmail={() => switchMode('EMAIL')}
        onPickFetch={() => {
          setFetchDefaultTab(undefined);
          switchMode('FETCH_HUB');
        }}
        onFetchCdsl={() => {
          setFetchDefaultTab('CDSL');
          switchMode('FETCH_HUB');
        }}
        onFetchKfintech={() => {
          setFetchDefaultTab('KFINTECH');
          switchMode('FETCH_HUB');
        }}
        onPickMutualFunds={() => {
          setSelectedType('CAMS_KFINTECH');
          setSelectedBroker(null);
          emitEvent('ASSET_SELECTED', { asset: 'MUTUAL_FUNDS' });
          // Skip the method picker when upload is the only viable path.
          const mfMethodWorthShowing =
            enableInbox || enableInboundEmail || enableGenerator;
          switchMode(mfMethodWorthShowing ? 'MF_METHOD' : 'FILE_UPLOAD');
        }}
        onPickDemat={() => {
          emitEvent('ASSET_SELECTED', { asset: 'DEMAT' });
          // Route to broker picker (the default demat path) so the user
          // sees the broker grid first — this is the brand-recognition
          // surface that drove conversion in v1. Embedders who opted out
          // (`showBrokerPicker: false`) skip straight to upload.
          if (showBrokerPicker) {
            switchMode('BROKER_PICKER');
          } else {
            const firstDemat = allowedTypes.find((t) => t === 'CDSL' || t === 'NSDL');
            if (firstDemat) setSelectedType(firstDemat);
            switchMode('FILE_UPLOAD');
          }
        }}
        onPickGmail={() => {
          setEmailDefaultTab('CONNECT');
          switchMode('EMAIL');
        }}
        onPickInboundEmail={() => {
          setEmailDefaultTab('FORWARD');
          switchMode('EMAIL');
        }}
        onFileDropped={(file) => {
          // Stash the dropped file and navigate to upload so the user lands
          // directly on the password step. UploadZone picks it up via
          // `initialFile`.
          setPendingFile(file);
          emitEvent('FILE_SELECTED', { filename: file.name, size: file.size });
          switchMode('FILE_UPLOAD');
        }}
      />
    );
  };

  const renderBrokerPicker = () => {
    const brokerList = config.brokers || DEFAULT_BROKERS;
    const manual = selectedBroker === 'Other';

    if (manual) {
      return (
        <div>
          <ScreenHeader
            title="Which depository?"
            description="Indian stocks are held in one of two government-authorised depositories. Most retail brokers (Zerodha, Groww, Upstox) use CDSL. Banks like HDFC, ICICI and Kotak typically use NSDL."
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <OptionCard
              title="CDSL"
              description="Central Depository Services Ltd — most retail brokers"
              accent="primary"
              onClick={() => {
                setSelectedType('CDSL');
                setCameViaBrokerPicker(true);
                emitEvent('BROKER_SELECTED', {
                  broker: 'Other',
                  depository: 'CDSL',
                });
                routeAfterBrokerSelected('CDSL');
              }}
            />
            <OptionCard
              title="NSDL"
              description="National Securities Depository Ltd — typically bank brokers"
              onClick={() => {
                setSelectedType('NSDL');
                setCameViaBrokerPicker(true);
                emitEvent('BROKER_SELECTED', {
                  broker: 'Other',
                  depository: 'NSDL',
                });
                routeAfterBrokerSelected('NSDL');
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div>
        <ScreenHeader
          title="Which broker is your demat with?"
          description="Pick yours below. Don't see it or not sure? Tap 'I don't know' — we'll figure it out from your file."
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
            maxHeight: 320,
            overflowY: 'auto',
            paddingRight: 2,
          }}
        >
          {brokerList.map((broker) => (
            <BrokerTile
              key={broker.name}
              name={broker.name}
              depository={broker.depository}
              logo={broker.logo}
              selected={selectedBroker === broker.name}
              onClick={() => {
                setSelectedBroker(broker.name);
                setSelectedType(broker.depository);
                setCameViaBrokerPicker(true);
                emitEvent('BROKER_SELECTED', {
                  broker: broker.name,
                  depository: broker.depository,
                });
                routeAfterBrokerSelected(broker.depository);
              }}
            />
          ))}
        </div>
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setSelectedBroker('Other')}
          style={{ marginTop: 12 }}
        >
          My broker isn't here / I don't know
        </Button>
      </div>
    );
  };

  // Uppercase section heading separating method-picker intent buckets.
  const SectionLabel: React.FC<{
    children: React.ReactNode;
    style?: React.CSSProperties;
  }> = ({ children, style }) => (
    <p
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: 'var(--pc-color-text-faded)',
        margin: '0 0 10px',
        textAlign: 'left',
        ...style,
      }}
    >
      {children}
    </p>
  );

  // Method picker shown after a broker is selected on the demat path.
  // Two buckets: "I have the file" (upload, forward) vs "Get it for
  // me" (CDSL OTP, Gmail).
  const renderDematMethod = () => {
    const brokerName = selectedBroker && selectedBroker !== 'Other' ? selectedBroker : null;
    const heading = brokerName || 'Stocks & demat';
    const isCdslBroker = selectedType === 'CDSL';
    const showCdslOtp =
      isCdslBroker && enableCdslFetch && allowedTypes.includes('CDSL');

    return (
      <div>
        <ScreenHeader title={heading} />

        <SectionLabel>I have the file</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <OptionCard
            icon={<IconUpload size={20} />}
            title="Upload your statement"
            description="PDF from your device"
            onClick={() => {
              setCameViaBrokerPicker(true);
              switchMode('FILE_UPLOAD');
            }}
          />
          {enableInboundEmail && (
            <OptionCard
              icon={<IconForwardEmail size={20} />}
              title="Forward the email"
              description="To a unique address we'll give you"
              onClick={() => {
                setEmailDefaultTab('FORWARD');
                switchMode('EMAIL');
              }}
            />
          )}
        </div>

        {(showCdslOtp || enableInbox) && (
          <>
            <SectionLabel style={{ marginTop: 18 }}>Get it for me</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {showCdslOtp && (
                <OptionCard
                  icon={<IconBolt size={20} />}
                  title="Fetch via CDSL OTP"
                  description="Full holdings (CDSL + NSDL) in seconds"
                  accent="primary"
                  onClick={() => {
                    setFetchDefaultTab('CDSL');
                    switchMode('FETCH_HUB');
                  }}
                />
              )}
              {enableInbox && (
                <OptionCard
                  icon={<IconInboxScan size={20} />}
                  title="Find in inbox"
                  description="We scan your inbox for CAS emails"
                  onClick={() => {
                    setEmailDefaultTab('CONNECT');
                    switchMode('EMAIL');
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Method picker for the Mutual Funds path. Same two-section layout
  // as demat; KFintech mailback replaces CDSL OTP under "Get it for me".
  const renderMfMethod = () => {
    const showGmail = enableInbox;
    const showMailback = enableGenerator;

    return (
      <div>
        <ScreenHeader title="Mutual funds" />

        <SectionLabel>I have the file</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <OptionCard
            icon={<IconUpload size={20} />}
            title="Upload your statement"
            description="PDF from your device"
            onClick={() => switchMode('FILE_UPLOAD')}
          />
          {enableInboundEmail && (
            <OptionCard
              icon={<IconForwardEmail size={20} />}
              title="Forward the email"
              description="To a unique address we'll give you"
              onClick={() => {
                setEmailDefaultTab('FORWARD');
                switchMode('EMAIL');
              }}
            />
          )}
        </div>

        {(showGmail || showMailback) && (
          <>
            <SectionLabel style={{ marginTop: 18 }}>Get it for me</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {showGmail && (
                <OptionCard
                  icon={<IconInboxScan size={20} />}
                  title="Find in inbox"
                  description="We scan your inbox for CAS emails"
                  accent="primary"
                  onClick={() => {
                    setEmailDefaultTab('CONNECT');
                    switchMode('EMAIL');
                  }}
                />
              )}
              {showMailback && (
                <OptionCard
                  icon={<IconGeneratorMailback size={20} />}
                  title="Email me a fresh CAS"
                  description="KFintech/CAMS sends it to your PAN email — usually under 2 minutes"
                  onClick={() => {
                    setFetchDefaultTab('KFINTECH');
                    switchMode('FETCH_HUB');
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Protect mid-flow states (upload-in-progress, OTP, mailback, inbound
  // polling, Gmail OAuth) from accidental dismissal via overlay click.
  const inMidFlow =
    isProcessing ||
    (mode === 'FETCH_HUB' && (generatorLoading || fetchDefaultTab !== undefined)) ||
    mode === 'EMAIL';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onBack={handleBack}
      showBackButton={isSubScreen && !classifiedError}
      isSandbox={isSandbox}
      width={440}
      theme={config.theme}
      confirmOnOverlayClose={
        inMidFlow
          ? 'Cancel and lose your progress? Your statement won\'t be imported.'
          : undefined
      }
    >
      <div
        style={{
          padding: '28px 22px 20px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1 }}>{renderContent()}</div>

        {!config.hideFooter && (
          <div
            style={{
              marginTop: 22,
              paddingTop: 14,
              borderTop: '1px solid var(--pc-color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: 'var(--pc-color-text-faded)',
            }}
          >
            <IconShield size={12} />
            <span style={{ fontSize: 11 }}>
              Secured by{' '}
              <a
                href="https://casparser.in"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--pc-color-text-faded)',
                  textDecoration: 'underline',
                }}
              >
                CASParser
              </a>
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

